import client from "services/ngage/api-client";
import {Element, ElementData} from "types/Element";
import deserializeElement from "services/ngage/deserializers/element";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

type ElementFilters = {
    search?: string | null
    page?: number
    perPage?: number
}

/**
 * Get all elements (paginated)
 */
const all = async (filters: ElementFilters) => {
    const params: Record<string, any> = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try{
        const page = (await client.get<DataResource<Element> | null>(`/elements`, {params})).data

        if(page) page.data = page.data.map(deserializeElement)

        return page
    } catch (error) {
        console.error("Unable to fetch elements", error)
        return null
    }
}

/**
 * Get all elements that match the given filters (paginated)
 */
const filtered = async (exhibitId: number, filters: ElementFilters) => {
    const params: Record<string, any> = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try{
        const page = (await client.get<DataResource<Element> | null>(`/exhibits/${exhibitId}/elements`, {params})).data

        if(page) page.data = page.data.map(deserializeElement)

        return page
    } catch (error) {
        console.error("Unable to fetch elements", error)
        return null
    }
}

/**
 * Get a single element
 *
 * @param {number} id
 */
const get = async (id: number) => {
    try{
        const element = (await client.get<Element | null>(`/elements/${id}`)).data
        return element? deserializeElement(element) : null
    } catch (error) {
        console.error(`Unable to retrieve element ${id}`, error)
        return null
    }
}

/**
 * Store a new element
 *
 * @param data
 */
const store = async (data: ElementData) => {
    try{
        const element = (await client.post<Element | null>('/elements', data)).data
        return element? deserializeElement(element) : null
    } catch (error) {
        console.error("Unable to store element", error)
        return null
    }
}

/**
 * Update an existing element
 *
 * @param {number} id
 * @param data
 */
const update = async (id: number, data: ElementData) => {
    try{
        const element = (await client.put<Element | null>(`/elements/${id}`, data)).data
        return element? deserializeElement(element) : null
    } catch (error) {
        console.error(`Unable to update element ${id}`, error)
        return null
    }
}

/**
 * Permanently delete a element
 *
 * @param {number} id
 */
const destroy = async (id: number) => {
    try{
        return (await client.delete<null>(`/elements/${id}`)).status === 204
    } catch (error) {
        console.error(`Unable to destroy element ${id}`, error)
        return false
    }
}

const elements = {
    all,
    filtered,
    get,
    store,
    update,
    destroy
}

export default elements;